import { useState } from 'react'
import Typography from '@mui/material/Typography'
import InputField from '../../../../../../FormControl/input/InputField'
import DialogConfirm from '../../../../../../dialog/DialogConfirm'
import useAnalyticsCurriculum from '../../../../../../../hooks/analytics/useAnalyticsCurriculum'
import { Alert } from '@mui/material'
import InputColor from '../../../../../../FormControl/input/InputColor'

const StatusDialog = ({ show, onClose, type, formData = {}, onReset }) => {
  const types = ['create', 'edit', 'delete', 'preview']
  const { createModule, updateModule, deleteModule } = useAnalyticsCurriculum()
  const [form, setInputForm] = useState(formData)
  const [errors, setInputErrors] = useState(null)
  const [loading, toggleLoading] = useState(false)

  const onChange = (name, value) => {
    setInputForm({ ...form, [name]: value })
    setInputErrors({ ...errors, [name]: null })
  }

  const onSave = async (e) => {
    if (!types.includes(type)) {
      setInputErrors({ message: 'Invalid type' })
      return
    }
    e.preventDefault()
    toggleLoading(true)
    try {
      let result = { error: false }
      if (type === 'create') {
        result = await createModule('status', form)
      }
      if (type === 'edit') {
        result = await updateModule('status', form._id, form)
      }
      if (type === 'delete') {
        result = await deleteModule('status', form._id)
      }
      if (result.error) {
        setInputErrors(result)
      } else {
        onClose()
        onReset()
      }
    } finally {
      toggleLoading(false)
    }
  }

  return (
    <DialogConfirm
      id={`${type}-status`}
      open={show}
      title={`${type} status`}
      onCancel={onClose}
      onConfirm={['create', 'edit', 'delete'].includes(type) ? onSave : undefined}
      textConfirm={type === 'delete' ? 'Confirm' : 'Save'}
      onCloseHeader={type === 'preview'}
      showActions={['create', 'edit', 'delete'].includes(type)}
      loading={loading}
      maxWidth='xs'
    >
      {types.includes(type) ? (
        <>
          {type === 'delete' ? (
            <Typography component={'div'}>
              Are you sure you want to delete the status: <b>{form.name}</b>?
            </Typography>
          ) : (
            <div className='flex flex-col gap-4 py-4'>
              <div className='grid grid-cols-3 items-start gap-4'>
                <Typography
                  variant='inherit'
                  className='font-bold'
                  color='primary'
                  align='right'
                  component='p'
                >
                  Position
                </Typography>
                <div className='col-span-2'>
                  {['preview', 'edit'].includes(type) ? (
                    <Typography>{form.position}</Typography>
                  ) : (
                    <InputField
                      id='position'
                      name='position'
                      value={form.position}
                      error={errors?.position}
                      helperText={errors?.position}
                      onChange={(e) => onChange('position', e.target.value)}
                      fullWidth
                    />
                  )}
                </div>
                <Typography
                  variant='inherit'
                  className='font-bold'
                  color='primary'
                  align='right'
                  component='p'
                >
                  Code
                </Typography>
                <div className='col-span-2'>
                  {['preview', 'edit'].includes(type) ? (
                    <Typography>{form.code}</Typography>
                  ) : (
                    <InputField
                      id='code'
                      name='code'
                      value={form.code}
                      error={errors?.code}
                      helperText={errors?.code}
                      onChange={(e) => onChange('code', e.target.value)}
                      fullWidth
                    />
                  )}
                </div>
                <Typography
                  variant='inherit'
                  className='font-bold'
                  color='primary'
                  align='right'
                  component='p'
                >
                  Name
                </Typography>
                <div className='col-span-2'>
                  {type === 'preview' ? (
                    <Typography>{form.name}</Typography>
                  ) : (
                    <InputField
                      id='name'
                      name='name'
                      value={form.name}
                      error={errors?.name}
                      helperText={errors?.name}
                      onChange={(e) => onChange('name', e.target.value)}
                      fullWidth
                    />
                  )}
                </div>
                <Typography
                  variant='inherit'
                  className='font-bold'
                  color='primary'
                  align='right'
                  component='p'
                >
                  Description
                </Typography>
                <div className='col-span-2'>
                  {type === 'preview' ? (
                    <Typography>{form.description}</Typography>
                  ) : (
                    <InputField
                      id='description'
                      name='description'
                      value={form.description}
                      error={errors?.description}
                      helperText={errors?.description}
                      onChange={(e) => onChange('description', e.target.value)}
                      fullWidth
                      multiline={true}
                    />
                  )}
                </div>
                <Typography
                  variant='inherit'
                  className='font-bold'
                  color='primary'
                  align='right'
                  component='p'
                >
                  Text Color
                </Typography>
                <div className='col-span-2'>
                  <InputColor
                    id='text-color'
                    name='color'
                    value={form.color}
                    error={errors?.color}
                    helperText={errors?.color}
                    onChange={(value) => onChange('color', value)}
                    fullWidth
                    disabled={type === 'preview'}
                  />
                </div>
                <Typography
                  variant='inherit'
                  className='font-bold'
                  color='primary'
                  align='right'
                  component='p'
                >
                  Background
                </Typography>
                <div className='col-span-2'>
                  <InputColor
                    id='bg-color'
                    name='bgColor'
                    value={form.bgColor}
                    error={errors?.bgColor}
                    helperText={errors?.bgColor}
                    onChange={(value) => onChange('bgColor', value)}
                    fullWidth
                    disabled={type === 'preview'}
                  />
                </div>
                <Typography
                  variant='inherit'
                  className='font-bold'
                  color='primary'
                  align='right'
                  component='p'
                >
                  Border
                </Typography>
                <div className='col-span-2'>
                  <InputColor
                    id='border'
                    name='border'
                    value={form.border}
                    error={errors?.border}
                    helperText={errors?.border}
                    onChange={(value) => onChange('border', value)}
                    fullWidth
                    disabled={type === 'preview'}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <Alert severity='error'>Invalid type</Alert>
      )}
      {errors && errors.message && (
        <Alert severity='error' onClose={() => setInputErrors({ message: null })}>
          {errors.message}
        </Alert>
      )}
    </DialogConfirm>
  )
}

export default StatusDialog
